import { ref } from 'vue';
import { trainingMatrixApi } from '@/entities/Training';
import type {
  TrainingMatrixDocumentPrefix,
  TrainingMatrixSectionDocument,
  TrainingMatrixSectionSearchField,
} from '@/entities/Training';

export interface TrainingMatrixSectionDocumentItem extends TrainingMatrixSectionDocument {
  id: number;
}

export interface TrainingMatrixDocumentSectionState {
  prefixCode: TrainingMatrixDocumentPrefix;
  title: string;
  items: TrainingMatrixSectionDocumentItem[];
  selectedItems: string[];
  search: string;
  searchField: TrainingMatrixSectionSearchField;
  loading: boolean;
}

export const TRAINING_MATRIX_DOCUMENT_SECTIONS: { prefixCode: TrainingMatrixDocumentPrefix; title: string }[] = [
  { prefixCode: 'MQ', title: 'Manual da Qualidade' },
  { prefixCode: 'PR', title: 'Procedimentos' },
  { prefixCode: 'IT', title: 'Instrução de Trabalho' },
  { prefixCode: 'DA', title: 'Dados' },
];

export function useTrainingMatrixDocumentSections() {
  const sections = ref<TrainingMatrixDocumentSectionState[]>(
    TRAINING_MATRIX_DOCUMENT_SECTIONS.map((config) => ({
      ...config,
      items: [],
      selectedItems: [],
      search: '',
      searchField: 'prefixo',
      loading: false,
    }))
  );

  async function fetchSection(index: number): Promise<void> {
    const section = sections.value[index];
    section.loading = true;
    try {
      const trimmed = section.search.trim();
      const result = await trainingMatrixApi.getSectionDocuments({
        prefixCode: section.prefixCode,
        search: trimmed || undefined,
        searchField: trimmed ? section.searchField : undefined,
      });
      section.items = result.data.map((document) => ({
        ...document,
        id: document.documentRevisionId,
      }));
    } finally {
      section.loading = false;
    }
  }

  async function fetchAllSections(): Promise<void> {
    await Promise.all(sections.value.map((_, index) => fetchSection(index)));
  }

  function updateSearch(index: number, term: string): void {
    sections.value[index].search = term;
    fetchSection(index);
  }

  function updateSearchField(index: number, field: TrainingMatrixSectionSearchField): void {
    sections.value[index].searchField = field;
  }

  function updateSelectedItems(index: number, ids: string[]): void {
    sections.value[index].selectedItems = ids;
  }

  /**
   * Distributes already-linked documents into the section matching their prefix — each
   * `code` (e.g. "MQ01.1") starts with the section's prefixCode, so a document can only ever
   * belong to exactly one section.
   */
  function setPreselectedDocuments(documents: { id: number; code: string }[]): void {
    sections.value.forEach((section) => {
      section.selectedItems = documents
        .filter((document) => document.code.startsWith(section.prefixCode))
        .map((document) => String(document.id));
    });
  }

  function getSelectedDocumentRevisionIds(): number[] {
    return sections.value.flatMap((section) => section.selectedItems.map(Number));
  }

  function clearSelection(): void {
    sections.value.forEach((section) => {
      section.selectedItems = [];
    });
  }

  return {
    sections,
    fetchAllSections,
    updateSearch,
    updateSearchField,
    updateSelectedItems,
    setPreselectedDocuments,
    getSelectedDocumentRevisionIds,
    clearSelection,
  };
}
